import { ArrowBack, FileDownloadOutlined } from '@mui/icons-material'
import { translate } from 'locales'
import { alertDanger } from 'utils/toast'
import { exportToExcel } from 'utils/exportExcel'
import { formatLocal, formatLocalCap } from 'utils/date'
import React, { useEffect, useState } from 'react'
import {
  fetchMonthlyAppointmentsForExport,
  findMonthlyAppointments,
} from '../services'
import { IFinancialMonthAppointments, IFinancialMonthRow } from '../types'
import { formatCurrency } from '../utils'
import ConsultationsTable from './ConsultationsTable'
import { consultationStatusLabelKey } from './consultationStatus'
import SummaryCards from './SummaryCards'
import * as S from './styles'

const PAGE_SIZE = 5

const initialAppointments = (
  row: IFinancialMonthRow
): IFinancialMonthAppointments => ({
  periodMonth: row.periodMonth,
  totalValue: row.totalValue,
  stats: row.stats,
  rates: {
    consultation30: 0,
    consultation60: 0,
    noShow30: 0,
    noShow60: 0,
    canceled30: 0,
    canceled60: 0,
  },
  rows: [],
  meta: { total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1, nextPage: false },
})

interface MonthlyHistoryDetailProps {
  detail: IFinancialMonthRow
  locale: string
  isPsychology: boolean
  isNutritionist: boolean
  isGeneralPractitioner: boolean
  showTotalPassRates: boolean
  onBack: () => void
}

const MonthlyHistoryDetail = ({
  detail,
  locale,
  isPsychology,
  isNutritionist,
  isGeneralPractitioner,
  showTotalPassRates,
  onBack,
}: MonthlyHistoryDetailProps) => {
  const monthLabel = formatLocalCap(`${detail.periodMonth}-01`, 'MMMM yyyy')

  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [appointments, setAppointments] = useState<IFinancialMonthAppointments>(
    () => initialAppointments(detail)
  )

  useEffect(() => {
    findMonthlyAppointments(
      detail.periodMonth,
      page,
      PAGE_SIZE,
      setAppointments,
      setLoading
    )
  }, [detail.periodMonth, page])

  const handleExport = async () => {
    if (appointments.meta.total === 0) return

    try {
      setExporting(true)
      const rows = await fetchMonthlyAppointmentsForExport(
        detail.periodMonth,
        appointments.meta.total
      )

      const totalValue = rows.reduce((acc, row) => acc + row.value, 0)

      exportToExcel({
        filename: `atendimentos-${detail.periodMonth}`,
        sheetName: translate('financial.monthlyHistory.detail.tableTitle'),
        headers: [
          translate('financial.monthlyHistory.detail.columnDate'),
          translate('financial.monthlyHistory.detail.columnPatient'),
          translate('financial.monthlyHistory.detail.columnSpecialty'),
          translate('financial.monthlyHistory.detail.columnDuration'),
          translate('financial.monthlyHistory.detail.columnValue'),
          translate('financial.monthlyHistory.detail.status'),
        ],
        rows: rows.map((row) => [
          formatLocal(row.datetime, 'dd/MM/yyyy HH:mm'),
          row.patientName,
          row.specialty,
          `${row.durationMinutes} min`,
          formatCurrency(row.value, locale),
          translate(consultationStatusLabelKey(row.status)),
        ]),
        footerRows: [
          [],
          [
            '',
            '',
            '',
            translate('financial.monthlyHistory.detail.total'),
            formatCurrency(totalValue, locale),
            '',
          ],
        ],
      })
    } catch (err) {
      console.error(err)
      alertDanger(translate('errors.errorTry'))
    } finally {
      setExporting(false)
    }
  }

  return (
    <S.Section>
      <S.BackButton type="button" onClick={onBack}>
        <ArrowBack />
        {translate('financial.monthlyHistory.detail.back')}
      </S.BackButton>

      <S.Header>
        <div>
          <h1>{monthLabel}</h1>
          <span className="section-subtitle">
            {translate('financial.monthlyHistory.detail.subtitle')}
          </span>
        </div>

        <S.ExportButton
          type="button"
          disabled={exporting || appointments.meta.total === 0}
          onClick={handleExport}
        >
          <FileDownloadOutlined />
          {translate('financial.monthlyHistory.detail.export')}
        </S.ExportButton>
      </S.Header>

      <SummaryCards
        totalValue={appointments.totalValue}
        stats={appointments.stats}
        rates={appointments.rates}
        status={detail.status}
        locale={locale}
        isPsychology={isPsychology}
        isNutritionist={isNutritionist}
        isGeneralPractitioner={isGeneralPractitioner}
        showTotalPassRates={showTotalPassRates}
      />

      <ConsultationsTable
        rows={appointments.rows}
        meta={appointments.meta}
        locale={locale}
        loading={loading}
        isGeneralPractitioner={isGeneralPractitioner}
        onPageChange={setPage}
      />
    </S.Section>
  )
}

export default MonthlyHistoryDetail
